import { jobApi } from '@/helpers';
import { defineStore } from 'pinia';
import { computed, ref, reactive, toRefs } from 'vue';
import axios from 'axios';
import to from 'await-to-js';
import {
  groupBy,
  max,
  values,
  set,
  each,
  merge,
  findIndex,
  keyBy,
  isEmpty,
  size,
  map,
  isEqual,
  orderBy,
  intersection,
  cloneDeep,
  includes,
  get,
  isNumber,
  forEach,
  keys,
  sumBy,
  partition,
  every,
  some,
  filter,
  uniqBy,
  sortBy,
  isNil,
  toLower,
  isBoolean,
  omit,
} from 'lodash';

// utils and plugins
import AgGridUtils from '@/utils/ag-grid-utils';
import i18n from '@/plugins/vue-i18n';
import handleErrorMessages from '@/stores/utils/validation';
import gridPreferences from '@/utils/grid-preferences';
import filterUtils from '@/utils/filter-utils';

// enums
import assortmentProductLockTypes from '@enums/assortment-product-lock-types';
import AssortmentCanvases from '@enums/assortment-canvases';
import productKpis from '@enums/product-kpis';

// stores
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useScenariosStore } from '@/stores/scenarios';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useClusteringStore } from '@/stores/clustering';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useContextStore } from '@/stores/context';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useFurnitureStore } from '@/stores/furniture';
import { useSnackbarStore } from '@/stores/snackbar';

// composables
import { useFeature } from '@/composables/use-feature';

// storage prefixes for column state - if you change these keys, existing user local storage objects will be invalid
const LIST_VIEW_COLUMN_STORAGE_PREFIX = 'listViewColumnState';
const LIST_VIEW_LAST_STATE_SAVED_SCENARIO_PREFIX = 'listViewLastStateSavedScenario';
const CANN_GROUPS_COLUMN_STORAGE_PREFIX = 'canvasCannGroupsColumnState';
const CANVAS_SELECTED_VIEW = 'canvasSelectedView';
const CANVAS_STICKY_HEADERS = 'canvasStickyHeaders';
const CANVAS_UNIFIED_COLUMNS = 'canvasUnifiedColumns';

/*
 * Function to return a set of product keys matching customer segments
 * segment data is set on each product like so
 * mainCustomerSegments: [
 *  {scheme: 'Price-Sensitivity', segment: 'Price-Focused'},
 *  {scheme: 'Residence-Type', segment: 'Urban'},
 * ]
 * Which indicates that this product is purchased by 'Price-Focused' customers in the
 * 'Price-Sensitivity' customer segmentation scheme and 'Urban' customers in the 'Residence-Type'
 * customer segmentation scheme.
 */
function getProductKeysMatchingCustomerSegmentsFilter(toFilter, products) {
  const customerSegmentFilters = filter(
    toFilter,
    f => f.type === AssortmentCanvases.Filters.CustomerSegment
  );
  if (!customerSegmentFilters.length) return;

  // Convert mainCustomerSegments to object like {scheme: segment}
  const productKeyMainSegmentsMap = products.reduce((acc, { productKey, mainCustomerSegments }) => {
    if (mainCustomerSegments) {
      acc[productKey] = mainCustomerSegments.reduce((schemeMainSegmentMap, { scheme, segment }) => {
        schemeMainSegmentMap[scheme] = segment;
        return schemeMainSegmentMap;
      }, {});
    }
    return acc;
  }, {});

  // Create the set
  const productKeysMatchingFilter = customerSegmentFilters.reduce((acc, filterObj) => {
    filterObj.values.forEach(({ customerSegmentScheme, customerSegment }) => {
      Object.entries(productKeyMainSegmentsMap).forEach(([productKey, mainSegmentsMap]) => {
        if (mainSegmentsMap[customerSegmentScheme] === customerSegment) {
          acc.add(+productKey);
        }
      });
    });
    return acc;
  }, new Set());
  return productKeysMatchingFilter;
}

function calculateProductsDeltas(productsOrSingleProduct, pod, referenceCheckpointProducts) {
  if (!isEmpty(pod)) {
    const noReferenceCheckpoint = isEmpty(referenceCheckpointProducts);
    // There will always be a current and last optimised
    // In the case that the optimiser hasn't run, they'll be the same and the delta will be 0.
    const products = Array.isArray(productsOrSingleProduct)
      ? productsOrSingleProduct
      : [productsOrSingleProduct];
    return products.map(product => {
      const podOfCurrent = pod[product.currentSpacebreakId];
      const podOfLastOptimised = pod[product.optimisedSpacebreakId];

      // If there's no reference checkpoint data, theproductsPerSpacebreak difference will always be 0
      // If there is one, subtract previous from current
      let referenceDelta = 0;
      if (!pod || !referenceCheckpointProducts) {
        referenceDelta = 0;
      } else {
        referenceDelta = noReferenceCheckpoint
          ? 0
          : podOfCurrent - pod[referenceCheckpointProducts[product.productKey]];
      }
      product.deltas = {
        reference: referenceDelta,
        lastOptimised: podOfCurrent - podOfLastOptimised,
      };

      return product;
    });
  }

  return Array.isArray(productsOrSingleProduct) ? productsOrSingleProduct : undefined;
}

/*
 * Function to return a delegate function with curried state to return a set which determines
 * which product keys should be highlighted based on their external ranking.
 * See AOV3-3202 for detailed spec
 */
function getDetermineExternallyRankedProductsToHighlightDelegate(
  canvas,
  spacebreaks,
  pod,
  assortmentCanvasProducts,
  clientConfig
) {
  function delegate(toFilter, filteredProducts) {
    const config = clientConfig.externalProductRanking; // Alias for ease of access

    // This is what we return, for each filter index that is an externalProductRanking we
    // add a set of products to highlight
    const filterMap = {};
    forEach(toFilter, (filterObj, filterIndex) => {
      if (filterObj.type === AssortmentCanvases.Filters.ExternalProductRanking) {
        filterMap[filterIndex] = new Set();
        const filterValuesSet = new Set(filterObj.values);
        const filteredProductKeys = new Set(filteredProducts.map(p => p.productKey));
        const productsWithExteralRanking = assortmentCanvasProducts.filter(
          p => 'externalProductRanking' in p
        );
        const uniqueCategoryIds = new Set(
          productsWithExteralRanking.map(p => p.externalProductRanking.categoryId)
        );

        // We want to sort the products with external ranking from the best by external ranking
        // to the worst by external ranking
        // If we only have one category we can sort by rank, otherwise we have to do a combined
        // sort using the cutUtility, with ties going to rank. We use 'desc', 'asc', because
        // with rank, a lower number is better, but with cutUtility a higher number is better
        let productsWithExternalRankingSorted;
        if (uniqueCategoryIds.size === 1) {
          productsWithExternalRankingSorted = orderBy(
            productsWithExteralRanking,
            p => p.externalProductRanking.rank
          );
        } else {
          productsWithExternalRankingSorted = orderBy(
            productsWithExteralRanking,
            [p => p.externalProductRanking.cutUtility, p => p.externalProductRanking.rank],
            ['desc', 'asc']
          );
        }

        // Get the top X external ranking
        if (filterValuesSet.has(AssortmentCanvases.FilterValues.TopXExternalRanking)) {
          const numToHighLight = Math.min(
            Math.ceil((productsWithExternalRankingSorted.length * config.topXPercentage) / 100),
            config.topXAbsoluteLimit
          );
          forEach(productsWithExternalRankingSorted.slice(0, numToHighLight), p => {
            if (filteredProductKeys.has(p.productKey)) filterMap[filterIndex].add(p.productKey);
          });
        }

        // Get the higher reference ranking
        if (filterValuesSet.has(AssortmentCanvases.FilterValues.HigherReferenceRanking)) {
          const totalPod = pod[spacebreaks[0]._id];
          const spacebreakLookup = keyBy(spacebreaks, '_id');
          const condition4Limit = Math.floor(
            (assortmentCanvasProducts.length * config.condition4Percentage) / 100
          );

          let i = 0;
          let extRank = 0;
          let cumSum = 0.0;
          forEach(productsWithExternalRankingSorted, p => {
            cumSum += p.size;
            extRank += 1; // Since optimiser rank starts at 1 we can add one here
            while (i < spacebreaks.length && cumSum > spacebreaks[i].fillOverride) {
              // Keep advancing our i pointer until we're either in the unlisted bay or
              // until we land in the next spacebreak that fits
              i += 1;
            }

            // Skip to next if this is not in the set of filtered products anyway
            if (!filteredProductKeys.has(p.productKey)) return true;

            const extSpacebreak = spacebreaks[i];
            const curSpacebreak = spacebreakLookup[p.currentSpacebreakId];

            // Condition 1: Is the external spacebreak 'better' than the current spacebreak
            // If either spacebreak is undefined (not listed) then the fill rank is the number
            // of spacebreaks + 1
            const extSpacebreakFillRank = get(extSpacebreak, 'fillRank', spacebreaks.length + 1);
            const curSpacebreakFillRank = get(curSpacebreak, 'fillRank', spacebreaks.length + 1);
            const condition1 = extSpacebreakFillRank < curSpacebreakFillRank;
            if (!condition1) return true;

            // Condition 2, for some clients we don't want to use this at all
            if (config.respectCondition2) {
              // Is (A - B) * 100 / C >= X, where
              // A = external POD, B = current POD, C = Total POD, X is a percentange (default 20)
              // I.e. there would be a >= X% increase in the number of stores the product would be
              // included in if it were placed by its external ranking
              const extPod = pod[get(extSpacebreak, '_id')] || 0;
              const curPod = pod[get(curSpacebreak, '_id')] || 0;
              const podDeltaPercentage = ((extPod - curPod) * 100) / totalPod;
              const condition2 = podDeltaPercentage >= config.condition2Percentage;
              if (!condition2) return true;
            }

            // Condition 3: Is the external rank better than the current rank
            // If this canvas has not been optimised we simply say condition 3 is true
            const curRank = p.rank;
            const condition3 = canvas.hasBeenOptimised ? extRank < curRank : true;
            if (!condition3) return true;

            // Condition 4: Does the external ranking put them in the top 50% of products
            const condition4 = extRank < condition4Limit;
            // If condition 4 starts being false, it will remain false => quit this loop to avoid
            // needless computations
            if (!condition4) return false;

            // If we are here, all conditions must have been met => add to products to highlight
            filterMap[filterIndex].add(p.productKey);
          });
        }
      }
    });
    return filterMap;
  }
  return delegate;
}

function applyProductFilters(
  products,
  toFilter,
  spacebreakFilters,
  determineExternallyRankedProductsToHighlightDelegate,
  productsSet
) {
  // If we are not using externally ranked products to highlight, this will simply be null
  const externallyRankedProductsToHighlightMap =
    determineExternallyRankedProductsToHighlightDelegate(toFilter, products);
  // Will fast return null if no filters are customer segment filters
  const productKeysMatchingCustomerSegmentsFilter = getProductKeysMatchingCustomerSegmentsFilter(
    toFilter,
    products
  );

  return products.filter(p => {
    const isVisible = every(toFilter, (f, filterIndex) => {
      const notEqualTo = f.operator === AssortmentCanvases.FilterOperators.NotEqual;
      if (f.type === AssortmentCanvases.Filters.ExternalProductRanking) {
        return externallyRankedProductsToHighlightMap[filterIndex].has(p.productKey) !== notEqualTo;
      }
      if (f.type === AssortmentCanvases.Filters.CustomerSegment) {
        return productKeysMatchingCustomerSegmentsFilter.has(p.productKey) !== notEqualTo;
      }

      if (f.type === AssortmentCanvases.Filters.PromotedDemoted) {
        const { lastOptimised, reference } = get(p, 'deltas', {});
        const option = f.values[0];

        switch (option) {
          case AssortmentCanvases.FilterValues.PromotedOptimisation:
            return lastOptimised > 0 !== notEqualTo;
          case AssortmentCanvases.FilterValues.DemotedOptimisation:
            return lastOptimised < 0 !== notEqualTo;
          case AssortmentCanvases.FilterValues.PromotedReferenceCheckpoint:
            return reference > 0 !== notEqualTo;
          case AssortmentCanvases.FilterValues.DemotedReferenceCheckpoint:
            return reference < 0 !== notEqualTo;
          default:
            return false;
        }
      }

      if (includes(spacebreakFilters, f.type)) {
        let hasMatch = includes(f.values, p[f.type]);

        if (!p[f.type]) {
          hasMatch = some(f.values, v =>
            v === AssortmentCanvases.FilterMappings.NotForAssortment
              ? !p.assortment || p.isLocal
              : v === !!p.isEligible && p.assortment && !p.isLocal
          );
        }
        return hasMatch !== notEqualTo;
      }

      // custom attributes are flattened to main object, so we can access their props directly
      if (f.type === AssortmentCanvases.Filters.customAttributes) f.type = f.attributeId;
      // isNewProduct is an optional field, so if not present, it is false
      if (f.type === AssortmentCanvases.Filters.ProductType) p[f.type] = p[f.type] || false;

      if (
        f.type === AssortmentCanvases.Filters.SBChangeDate ||
        f.type === AssortmentCanvases.Filters.ChainIntroDate
      ) {
        const shouldFilterProduct = filterUtils.shouldCanvasProductBeFilteredOutByDate({
          productDateValue: p[f.type],
          filterOperator: f.operator,
          filterDateValues: f.values,
        });

        // Only return the filter result if true/false was returned. Fall through if undefined etc were returned.
        if (isBoolean(shouldFilterProduct)) {
          return shouldFilterProduct;
        }
      }

      return includes(f.values, p[f.type]) !== notEqualTo;
    });
    if (isVisible) productsSet.add(p.productKey);

    return isVisible;
  });
}

// Function to get KPI title to display in header
function getKpiTitle(kpi) {
  const contextStore = useContextStore();

  if (kpi === productKpis.kpis.SALES) {
    return i18n.t(`assortmentCanvas.${kpi}`, {
      currency: i18n.t(`currencies.${contextStore.numericLocale}`),
    });
  }
  return i18n.t(`assortmentCanvas.${kpi}`);
}

export const useAssortmentCanvasStore = defineStore('assortmentCanvas', () => {
  // Stores
  const scenariosStore = useScenariosStore();
  const clusteringStore = useClusteringStore();
  const furnitureStore = useFurnitureStore();
  const contextStore = useContextStore();
  const workpackagesStore = useWorkpackagesStore();
  const snackbarStore = useSnackbarStore();

  // State
  const state = reactive({
    assortmentCanvases: [],
    workpackageAssortmentCanvases: [],
    workpackageCheckpoints: [],
    finalWorkpackageIds: new Set(),
    finalScenarioIds: new Set(),
    assortmentCheckpoints: [],
    scenarioCheckpoints: [],
    selectedAssortmentCanvas: {},
    selectedPod: {},
    assortmentCanvasProducts: [],
    spacebreaks: [],
    activeSpacebreak: null,
    spacebreaksOrderedObject: {},
    loading: false,
    finalCheckpointLoading: false,
    selectedView: AssortmentCanvases.Views.Tile,
    expandedCdts: {},
    childTrees: {},
    additionalAttributeColumns: [],
    additionalKPIsColumns: [],
    tileSize: {
      medium: {
        productImage: {
          height: 72,
          width: 72,
        },
        tile: {
          height: 80,
          width: 80,
        },
        tileContainer: {
          'min-width': 190,
          'max-width': 460,
        },
      },
      large: {
        productImage: {
          height: 102,
          width: 102,
        },
        tile: {
          height: 110,
          width: 110,
        },
        tileContainer: {
          'min-width': 250,
          'max-width': 610,
        },
      },
    },
    marginAroundTile: 9,
    referenceCheckpoint: null,
    selectedReferenceCheckpoint: null,
    referenceCheckpointProducts: [],
    storeClassId: null,
    popUpZindex: 7,
    borderWidth: 1,
    sidebarHeaderHeight: 85, // default height of sidebar header
    listViewContainerWidth: {
      medium: {
        default: 1100,
        base: 650,
      },
      large: {
        default: 1100,
        base: 650,
      },
    },
    selectedTileSize: 'medium',
    spacebreakSettings: {
      spacebreak: { _id: 'c00000000000000000000002' },
      open: false,
    },
    constraintEditing: {
      constraint: {
        rowNodeId: '',
        open: ref(false),
        rowNodeData: ref({}),
      },
    },
    listViewColumns: [],
    listViewFixedColumns: ['optimisedRank', 'productKeyDisplay', 'itemDescription'],
    // have a state for cann groups columns on canvas (not the same as scenario cann groups)
    // to manage what CDTs to display in CDT display mode
    canvasCannGroupColumns: [],
    canvasRenderingStatus: false,
    canvasProductsFilters: [],
    canvasProductsFiltersSelection: [],
    canvasProductsHighlights: [],
    showFilterLegend: false,
    productImages: {},
    fullScreenExpanded: false,
    stickyHeaders: true,
    unifiedColumns: {
      tile: false,
      list: false,
    },
    selectedDashboardProduct: null,
    showDashboard: false,
    isCanvasProductDragInProgress: false,
    activeCanvasProductDragOwnerId: null,
    selectedProductTopPartners: [],
    selectedProductTransferredSpend: {},
    constraintsCopyTarget: {},
    selectedConstraintsToCopy: {},
    isProductSidebarOpen: false,
    openedProductTooltips: [], // tracks which product tile tooltips are currently open
    numberOfRowsInGrid: 0,
    saveInProgress: false,
    displayBy: AssortmentCanvases.DisplayBy.cdt,
    constraintsValidationOccurred: {},
  });

  function isWorkpackageFinal(workpackageId) {
    return state.finalWorkpackageIds.has(workpackageId);
  }

  function isScenarioFinal(scenarioId) {
    return state.finalScenarioIds.has(scenarioId);
  }

  // Getters
  const isFinal = computed(() => params => {
    const menuLevelValue = get(params, 'data.menuLevel');

    const isFinalHandlers = {
      bundle: () => false, // Bundles are never final
      workpackage: () => isWorkpackageFinal(params.data._id),
      scenario: () => isScenarioFinal(params.data._id),
      canvas: () => params.data.isFinalCheckpoint,
      checkpoint: () => params.data.isFinalCheckpoint,
    };

    const finalHandler = isFinalHandlers[menuLevelValue];

    return finalHandler?.() || false;
  });

  const canvases = computed(() => state.assortmentCanvases);

  const canvasProducts = computed(() => state.assortmentCanvasProducts);

  const getSelectedProductTransferredSpend = computed(() => {
    return cloneDeep(state.selectedProductTransferredSpend);
  });

  const getPointsOfDistribution = computed(() => {
    // Points of distribution are the stores that utilise furniture contained in a spacebreak.
    // For each spacebreak, we get the furniture it contains and then sum them.
    // We get a cumulative sum from the largest spacebreak to the smallest.
    // This reflects the russian doll nesting of spacebreaks. If the smallest spacebreak contains
    // a piece of furniture, then all larger ones will as well.sorted
    const selectedClusterScheme = cloneDeep(clusteringStore.selectedClusterScheme);
    const clusters = get(selectedClusterScheme, 'clusters', []);
    // need this for the unclustered points of distribution
    clusters.push({ clusterId: null, storeKeys: [] });

    const getPointsOfDistributionPerStoreClass = (storeClassSpacebreaks, clusterStoreKeys) => {
      const clonedSpacebreaks = cloneDeep(storeClassSpacebreaks);
      // Sum from the right so you reflect russian doll nesting of spacebreaks
      const sortedSpacebreaks = orderBy(clonedSpacebreaks, 'fillOverride', 'desc');

      const { pod: pointsOfDistribution } = sortedSpacebreaks.reduce(
        (acc, sb) => {
          sb.storeKeys =
            clusterStoreKeys.length > 0
              ? intersection(sb.storeKeys, clusterStoreKeys)
              : sb.storeKeys;
          const storeCount = sb.storeKeys.length;
          const currentSum = storeCount + acc.previousStoreSum;
          acc.previousStoreSum = currentSum;
          return set(acc, `pod.${sb._id}`, currentSum);
        },
        // Null is initialised to 0 to handle the unassigned spacebreak
        { pod: { null: 0 }, previousStoreSum: 0 }
      );

      return pointsOfDistribution;
    };

    const podsPerClusterStoreClass = {};
    const storeClasses = furnitureStore.scenarioFurniture.storeClasses;
    clusters.forEach(c => {
      storeClasses.forEach(sc => {
        if (!podsPerClusterStoreClass[c.clusterId]) {
          podsPerClusterStoreClass[c.clusterId] = {
            [sc._id]: getPointsOfDistributionPerStoreClass(sc.spacebreaks, c.storeKeys),
          };
        } else {
          podsPerClusterStoreClass[c.clusterId][sc._id] = getPointsOfDistributionPerStoreClass(
            sc.spacebreaks,
            c.storeKeys
          );
        }
      });
    });
    return podsPerClusterStoreClass;
  });

  const getDashboardProduct = computed(() => {
    return state.selectedDashboardProduct;
  });

  const isCanvasProductDragInProgress = computed(
    () => state.activeCanvasProductDragOwnerId !== null
  );

  const sortByProductsInCanvas = computed(() => {
    return (a, b) => {
      const { hasBeenOptimised } = state.selectedAssortmentCanvas;
      const bothAreUnlisted = !a.currentSpacebreakId && !b.currentSpacebreakId;

      if (!hasBeenOptimised || bothAreUnlisted) {
        return AgGridUtils.sortings.naturalSort(a.productKeyDisplay, b.productKeyDisplay);
      }
      if (!a.currentSpacebreakId) return 1;
      if (!b.currentSpacebreakId) return -1;

      // sort ascending
      return a.optimisedRank - b.optimisedRank;
    };
  });

  const productsPerSpacebreak = computed(() =>
    groupBy(state.assortmentCanvasProducts, 'currentSpacebreakId')
  );

  const spaceUsedPerSpacebreak = computed(() => {
    const { spacebreakSizes } = state.spacebreaks.reduce(
      (acc, c) => {
        const products = filter(productsPerSpacebreak.value[c._id], ({ palletContent }) =>
          isEmpty(palletContent)
        );
        const currentSpacebreakSum = sumBy(products, 'size');

        const currentSum = currentSpacebreakSum + acc.previousSum;
        acc.previousSum = currentSum;
        return set(acc, `spacebreakSizes.${c._id}`, currentSum);
      },
      { spacebreakSizes: {}, previousSum: 0 }
    );

    return spacebreakSizes;
  });

  const getProductsWithNoSpacebreak = computed(() => {
    return products => {
      // Sorts all products that are not in any spacebreak by type.
      const sortedProducts = (products || []).sort(sortByProductsInCanvas.value);
      const [forAssortment, notForAssortment] = partition(
        sortedProducts,
        p => p.assortment && !p.isLocal
      );
      const [notListed, notInStoreClass] = partition(forAssortment, 'isEligible');
      return {
        notListed,
        notForAssortment,
        notInStoreClass,
      };
    };
  });

  const checkpoints = computed(() => {
    return state.assortmentCheckpoints.map(
      ({
        checkpointMeta,
        _id,
        isObserved,
        forecastParameters,
        forecastResults,
        jobs,
        reference,
        isNotRevertable,
        isFinalCheckpoint,
      }) => ({
        _id,
        isObserved,
        forecastParameters,
        forecastResults,
        checkpoint: checkpointMeta,
        jobs,
        reference,
        isNotRevertable,
        isFinalCheckpoint,
      })
    );
  });

  const { finalCheckpointLockEnabled } = useFeature();

  const isCanvasLockedByFinalCheckpoint = computed(() => {
    return (
      finalCheckpointLockEnabled.value && state.selectedAssortmentCanvas?.isFinalCheckpoint === true
    );
  });

  const selectedCanvasLockedSpacebreaks = computed(
    () =>
      new Set(
        state.selectedAssortmentCanvas.spacebreaks.filter(sb => sb.isLocked).map(({ _id }) => _id)
      )
  );

  const isSpacebreakLocked = computed(() => id => {
    return selectedCanvasLockedSpacebreaks.value.has(id);
  });

  const isSpacebreakLockToggleDisabled = computed(() => spacebreakId => {
    const lockedSpacebreaks = [...selectedCanvasLockedSpacebreaks.value];
    const spacebreakRank = state.spacebreaksOrderedObject[spacebreakId].rank;
    return lockedSpacebreaks.some(id => state.spacebreaksOrderedObject[id].rank > spacebreakRank);
  });

  const totalExpandedHeaderDepth = computed(() => {
    return state.unifiedColumns[state.selectedView] ? 1 : max([max(values(state.expandedCdts)), 1]);
  });

  const workpackageAssortmentCanvasesScenarioMap = computed(() => {
    return keyBy(state.workpackageAssortmentCanvases, 'scenarioId');
  });

  const scenarioCheckpointsById = computed(() => {
    return keyBy(state.scenarioCheckpoints, '_id');
  });

  const scenarioCheckpointsByCanvasId = computed(() => {
    /**
     * We need to create a map of canvasId to a list of all checkpoints for that canvas
     * for every canvas in the scenario.
     * {
     *  canvasAId: [...listOfCheckpoints],
     *  canvasBId: [...listOfCheckpoints],
     * }
     */
    const canvasIdmap = state.assortmentCanvases.reduce(
      (acc, canvas) => ({
        ...acc,
        [`${canvas.clusterId}-${canvas.storeClassId}`]: canvas._id,
      }),
      {}
    );
    if (isEmpty(canvasIdmap)) return {};
    return state.scenarioCheckpoints.reduce((acc, checkpoint) => {
      const canvasIdForCheckpoint =
        canvasIdmap[`${checkpoint.clusterId}-${checkpoint.storeClassId}`];
      acc[canvasIdForCheckpoint] = [...(acc[canvasIdForCheckpoint] || []), checkpoint];
      return acc;
    }, {});
  });

  const getCanvasProductsFiltersToFilter = computed(() => {
    return state.canvasProductsFilters.map(pf => ({
      operator: pf.operator,
      type: pf.filterType,
      attributeId: pf.attributeId,
      values:
        pf.filterType === AssortmentCanvases.Filters.CustomerSegment
          ? pf.filterValues
          : map(pf.filterValues, 'id'),
    }));
  });

  const getFilteredProducts = computed(() => {
    let res;
    if (!size(state.canvasProductsFilters)) {
      res = {
        visibleProductsSet: new Set(map(state.assortmentCanvasProducts, 'productKey')),
        products: state.assortmentCanvasProducts,
      };
    } else {
      const spacebreakFilters = [
        AssortmentCanvases.FilterMappings.CurrentSpacebreak,
        AssortmentCanvases.FilterMappings.OptimisedSpacebreak,
        AssortmentCanvases.FilterMappings.OriginalSpacebreak,
      ];
      const visibleProductsSet = new Set([]);
      const products = applyProductFilters(
        state.assortmentCanvasProducts,
        getCanvasProductsFiltersToFilter.value,
        spacebreakFilters,
        getDetermineExternallyRankedProductsToHighlightDelegate(
          state.selectedAssortmentCanvas,
          state.spacebreaks,
          state.selectedPod,
          state.assortmentCanvasProducts,
          contextStore.clientConfig,
          null
        ),
        visibleProductsSet
      );
      res = {
        visibleProductsSet,
        products,
      };
    }

    // Remove pallets from view
    state.assortmentCanvasProducts.forEach(({ palletContent, productKey }) => {
      if (!isEmpty(palletContent)) {
        res.visibleProductsSet.delete(productKey);
      }
    });
    return res;
  });

  const getCanvasProductsHighlightsToFilter = computed(() => {
    return state.canvasProductsHighlights.map(pf => ({
      operator: pf.operator,
      type: pf.filterType,
      attributeId: pf.attributeId,
      values:
        pf.filterType === AssortmentCanvases.Filters.CustomerSegment
          ? pf.filterValues
          : map(pf.filterValues, 'id'),
    }));
  });

  /** calls getFilteredProducts first and then figures out which of the filtered products to highlight */
  const getHighlightedProducts = computed(() => {
    const spacebreakFilters = [
      AssortmentCanvases.FilterMappings.CurrentSpacebreak,
      AssortmentCanvases.FilterMappings.OptimisedSpacebreak,
      AssortmentCanvases.FilterMappings.OriginalSpacebreak,
    ];
    const filteredProducts = getFilteredProducts.value;
    if (!size(state.canvasProductsHighlights)) {
      return {
        visibleProductsSet: filteredProducts.visibleProductsSet,
        products: filteredProducts.products,
        highlightedProductsSet: new Set([]),
        highlightedProducts: null,
      };
    }
    const highlightedProductsSet = new Set([]);
    const highlightedProducts = applyProductFilters(
      filteredProducts.products,
      getCanvasProductsHighlightsToFilter.value,
      spacebreakFilters,
      getDetermineExternallyRankedProductsToHighlightDelegate(
        state.selectedAssortmentCanvas,
        state.spacebreaks,
        state.selectedPod,
        state.assortmentCanvasProducts,
        contextStore.clientConfig,
        null
      ),
      highlightedProductsSet
    );

    return {
      visibleProductsSet: filteredProducts.visibleProductsSet,
      products: filteredProducts.products,
      highlightedProductsSet,
      highlightedProducts,
    };
  });

  const getIndexedCanvasProducts = computed(() => {
    return keyBy(state.assortmentCanvasProducts, 'productKey');
  });

  const isCanvasConfigInvalidForOptimisation = computed(() => {
    const invalidAccordingTo = isEmpty(
      get(scenariosStore.selectedScenario, 'optimiserSettings.optimiseAccordingTo', '')
    );
    const validUnitVolume = isNumber(
      get(scenariosStore.selectedScenario, 'optimiserSettings.utilityWeights.unitVolume', null)
    );
    const validSales = isNumber(
      get(scenariosStore.selectedScenario, 'optimiserSettings.utilityWeights.sales', null)
    );
    const validMargin = isNumber(
      get(scenariosStore.selectedScenario, 'optimiserSettings.utilityWeights.margin', null)
    );
    const validShrink =
      (isNumber(
        get(scenariosStore.selectedScenario, 'optimiserSettings.utilityWeights.shrink', null)
      ) &&
        workpackagesStore.isFreshWP) ||
      !workpackagesStore.isFreshWP;
    return invalidAccordingTo || !validUnitVolume || !validSales || !validMargin || !validShrink;
  });

  const wpCheckpointsMap = computed(() => {
    // create a mapping object of checkpoints {scenarioId: {clusterId: [checkpoints] }}
    const grouped = groupBy(state.workpackageCheckpoints, 'scenarioId');
    forEach(grouped, (value, key) => {
      grouped[key] = groupBy(grouped[key], 'clusterId');
    });
    return grouped;
  });

  const wpCanvasesMap = computed(() => {
    // create a mapping object of canvases {scenarioId: {clusterId: [canvases] }}
    const grouped = groupBy(state.workpackageAssortmentCanvases, 'scenarioId');
    forEach(grouped, (value, key) => {
      grouped[key] = groupBy(grouped[key], 'clusterId');
    });
    return grouped;
  });

  const canvasesById = computed(() => {
    return keyBy(state.assortmentCanvases, '_id');
  });

  const getProductTag = () => product => {
    return product.modellingType;
  };

  const productsByCdtGroup = computed(() => {
    return groupBy(state.assortmentCanvasProducts, 'cdtGroupId');
  });

  const hasConstraintsToCopySelected = computed(() => {
    return size(state.selectedConstraintsToCopy) > 0;
  });

  const isConstraintSelectedToCopy = computed(() => ({ constraintId }) => {
    const spacebreakId = state.selectedConstraintsToCopy[constraintId];

    return spacebreakId === null || !isEmpty(spacebreakId);
  });

  const constraintsToCopyById = computed(() => {
    return keyBy(state.constraintsCopyTarget.constraints.definitions, 'id');
  });

  const canvasProductsPallets = computed(() => {
    return groupBy(state.assortmentCanvasProducts, 'palletContent');
  });

  const selectedCanvas = computed(() => {
    return state.selectedAssortmentCanvas;
  });

  const getShowDashboard = computed(() => {
    return state.showDashboard;
  });

  const getSelectedProductTopPartners = computed(() => {
    return cloneDeep(state.selectedProductTopPartners);
  });

  const availableProductAttributesValues = computed(() => attributeName => {
    const options = uniqBy(state.assortmentCanvasProducts, attributeName)
      .filter(p => p[attributeName])
      .map(p => ({
        name: p[attributeName],
        id: p[attributeName],
      }));

    return sortBy(options, option => toLower(option.id));
  });

  const availableProductSeries = computed(() => {
    return availableProductAttributesValues.value('productSeries');
  });

  /* List view columns logic */

  const filteredListViewKpiColumns = computed(() => {
    // filter out rank and utility from selectedKpis
    // rank column is at start of list-view, utlity not used on list-view
    return contextStore.clientConfig.selectedKpis.filter(
      kpi => kpi !== 'rank' && kpi !== 'utility'
    );
  });

  const listViewExtraColumns = computed(() => {
    const { isVolumeRoSEnabled } = useFeature();
    if (!selectedCanvas.value) {
      return [];
    }

    const extraAttributeIds = get(
      state.additionalAttributeColumns,
      [selectedCanvas.value._id],
      []
    ).map(attr => attr.id);

    const extraKPIIds = get(state.additionalKPIsColumns, [selectedCanvas.value._id], []).map(
      kpi => kpi.id
    );

    // Add extraKPIs columns
    let extraKPICols = (contextStore.clientConfig.extraKPIs || []).map(kpi => ({
      id: kpi,
      title: getKpiTitle(kpi),
      type: 'kpi',
      visible: extraKPIIds.includes(kpi), // Set visible flag based on extraKPIs
    }));

    // Filter out volumeRoS if feature flag is disabled
    if (!isVolumeRoSEnabled.value) {
      extraKPICols = extraKPICols.filter(col => col.id !== 'volumeRoS');
    }

    // Add shrinkToSalesRatio KPI column if workpackage is fresh
    if (workpackagesStore.isFreshWP) {
      extraKPICols.push({
        id: 'shrinkToSalesRatio',
        title: getKpiTitle('shrinkToSalesRatio'),
        type: 'kpi',
        visible: true,
      });
    }

    // TODO why are we getting scenario attributes from clustering store? This seems wrong
    const scenarioAttributes = sortBy(clusteringStore.getScenarioAttributes, item =>
      item.name.toLowerCase()
    );
    const extraAttributeCols = scenarioAttributes.map(attr => ({
      id: attr.id,
      title: attr.name,
      type: 'attr',
      visible: extraAttributeIds.includes(attr.id), // Set visible flag based on extraAttributes
    }));

    // make extra KPIs next to existing KPIs
    return extraKPICols.concat(extraAttributeCols);
  });

  // Building the initial columns for canvas list view header layout dynamically
  // This is placed in the store since 2 components need to use the same logic
  // TODO is to change list-view to use AG Grid - then this logic can be moved back down to component
  const initialListViewColumns = computed(() => {
    const hasProductLineEnabled = get(
      contextStore.clientConfig,
      'features.scenario.products.isProductLineEnabled',
      false
    );

    const transactionWeightEnabled = get(
      contextStore.clientConfig,
      'features.transactionWeightEnabled',
      false
    );

    let baseColumns = [];
    const unifiedColumns = state.unifiedColumns[state.selectedView];

    if (contextStore.clientConfig.selectedKpis.includes('rank')) {
      baseColumns.push({
        id: 'optimisedRank',
        visible: true,
        title: i18n.t('assortmentCanvasPage.listViewPage.rank'),
        headerClass: `header-col pr-1 ${!unifiedColumns ? 'col-xs' : ''}`,
        cellClass: `pt-1 pb-1 pr-1 font-weight-bold entry-col ${!unifiedColumns ? 'col-xs' : ''}`,
        width: 70,
      });
    }

    // Add display name and description columns
    baseColumns = baseColumns.concat([
      {
        id: 'productKeyDisplay',
        visible: true,
        title: i18n.t('assortmentCanvasPage.listViewPage.id'),
        headerClass: `header-col id-col ${!unifiedColumns ? 'col-short' : ''}`,
        cellClass: 'pt-1 pb-1 entry-col id-col',
        width: 100,
      },
      {
        id: 'itemDescription',
        visible: true,
        title: i18n.t('assortmentCanvasPage.listViewPage.description'),
        headerClass: 'header-col description-col',
        cellClass: 'pt-1 pb-1 entry-col description-col',
        width: 260,
      },
      // Column for lock icon and points of distribution deltas
      {
        id: 'rowIcons',
        visible: true,
        title: i18n.t('assortmentCanvasPage.listViewPage.tags'),
        headerClass: 'header-col icons-col',
        cellClass: 'd-flex icons-col icons-container-list-view pa-0',
        width: 135,
      },
    ]);

    if (hasProductLineEnabled) {
      baseColumns.push({
        id: 'productLine',
        visible: true,
        title: i18n.t('general.productLine'),
        headerClass: `header-col ${!unifiedColumns ? 'col-short' : ''}`,
        cellClass: `entry-col pt-1 pb-1 ${!unifiedColumns ? 'col-short' : ''}`,
        width: 100,
      });
    }

    baseColumns.push({
      id: 'contentValue',
      visible: true,
      title: i18n.t('assortmentCanvasPage.listViewPage.combinedUnitOfMeasure'),
      headerClass: `header-col ${!unifiedColumns ? 'col-short' : ''}`,
      cellClass: `entry-col pt-1 pb-1 ${!unifiedColumns ? 'col-short' : ''}`,
      width: 100,
    });

    const filteredKpis = filteredListViewKpiColumns.value.map(kpi => ({
      id: kpi,
      visible: true,
      title: getKpiTitle(kpi),
      headerClass: `header-col ${!unifiedColumns ? 'col-short' : ''}`,
      cellClass: `entry-col pt-1 pb-1 ${!unifiedColumns ? 'col-short' : ''}`,
      width: 70,
    }));

    const extraColumns = listViewExtraColumns.value.map(column => {
      return {
        ...column,
        headerClass: `header-col extra-col ${!unifiedColumns ? 'extra-col-short' : ''} ${
          column.type !== 'kpi' ? 'attribute-col' : ''
        }`,
        cellClass: `entry-col pt-1 pb-1 extra-col attribute-col ${
          !unifiedColumns ? 'extra-col-short' : ''
        }`,
        width: 130,
      };
    });

    const transactionWeightColumn = transactionWeightEnabled
      ? [
          {
            id: 'transactionWeight',
            visible: true,
            title: i18n.t('assortmentCanvas.volumeUom'),
            headerClass: `header-col extra-col ${!unifiedColumns ? 'col-short' : ''}`,
            cellClass: `entry-col pt-1 pb-1 ${!unifiedColumns ? 'col-short' : ''}`,
            width: 70,
          },
        ]
      : [];

    const deltaTagColumn = [
      {
        id: 'deltaTag',
        visible: true,
        title: i18n.t('assortmentCanvasPage.listViewPage.optimisationDeltas'),
        headerClass: 'header-col list-view-tags-col',
        cellClass: 'entry-col pt-1 pb-1 d-flex justify-end list-view-tags-col',
        width: 135,
      },
    ];

    return baseColumns.concat(filteredKpis, extraColumns, transactionWeightColumn, deltaTagColumn);
  });

  // Initial state ofcanvas cann groups (CDT view) column headers
  // Need to keep in store since it defines what products to display, and that affects several other components
  // Not the same as scenarios store -> state.cannGroups, as this should only affect canvas
  const initialCanvasCannGroupColumns = computed(() => {
    // scenariosStore.cannGroups gives us leaf cann groups in a flat structure, which is what we need
    const scenarioCannGroups = scenariosStore.cannGroups;
    return scenarioCannGroups.map(cannGroup => ({
      ...cannGroup,
      visible: true,
    }));
  });

  const visibleListViewColumns = computed(() => {
    return state.listViewColumns.filter(col => col.visible);
  });

  const visibleCanvasCannGroups = computed(() => {
    return state.canvasCannGroupColumns.filter(col => col.visible);
  });

  const constraintsValidationOccurred = computed(() => state.constraintsValidationOccurred);

  // Actions

  function updateAssortmentCanvasProductById(assortmentCanvasProduct) {
    const index = findIndex(state.assortmentCanvasProducts, { _id: assortmentCanvasProduct._id });
    const product = state.assortmentCanvasProducts[index];
    if (product) {
      // defensive programming is in order
      forEach(keys(assortmentCanvasProduct), acpKey =>
        // need to set the product individually instead of whole array to avoid unnecessary reactivity.
        {
          product[acpKey] = assortmentCanvasProduct[acpKey];
        }
      );
      // need to calculate deltas after previous operation to ensure it does not get mistakenly overridden.
      calculateProductsDeltas(product, state.selectedPod, state.referenceCheckpointProducts);
    }
  }

  function updateAssortmentCanvasProducts(assortmentCanvasProductsParam) {
    state.assortmentCanvasProducts = state.assortmentCanvasProducts.map(originalProduct => {
      const updatedProduct = assortmentCanvasProductsParam.find(
        product => product._id === originalProduct._id
      );
      return updatedProduct || originalProduct;
    });
    calculateProductsDeltas(
      state.assortmentCanvasProducts,
      state.selectedPod,
      state.referenceCheckpointProducts
    );
  }

  function setAssortmentCanvasProducts(assortmentCanvasProductsLocal) {
    // Shows deltas in points of distribution for both the reference and recent optimised checkpoints
    // Returns a 0 in case of either checkpoint not existing
    state.assortmentCanvasProducts = calculateProductsDeltas(
      assortmentCanvasProductsLocal,
      state.selectedPod,
      state.referenceCheckpointProducts
    );
  }

  function setTileSize(tileSize) {
    state.selectedTileSize = tileSize;
  }

  function setSelectedView(view) {
    state.selectedView = view;

    gridPreferences.saveColumnState({ namePrefix: CANVAS_SELECTED_VIEW }, view);
    // reset popUpZindex on view change
    state.popUpZindex = 2;
  }

  function setStickyHeaders(value) {
    state.stickyHeaders = value;

    gridPreferences.saveColumnState(
      {
        namePrefix: CANVAS_STICKY_HEADERS,
        stateId: null,
      },
      value
    );
  }

  function setUnifiedColumns(newValues) {
    // We have different settings for tile/ list. Ensure we preserve the merge here.
    state.unifiedColumns = {
      ...state.unifiedColumns,
      ...newValues,
    };

    gridPreferences.saveColumnState({ namePrefix: CANVAS_UNIFIED_COLUMNS }, state.unifiedColumns);
  }

  /**
   * Fetches user defaults from localStorage and applies them. Should be called on canvas page load.
   * Note that these are global defaults, applied everywhere.
   * @function setCanvasUserDefaults
   */
  function setCanvasUserDefaults() {
    // 1. Pull user-specific config from localStorage and apply it if it exists
    // 2. If there were no user-defaults, try to apply configuration defaults
    const defaults = {
      [CANVAS_SELECTED_VIEW]: { setterFunction: setSelectedView },
      [CANVAS_STICKY_HEADERS]: { setterFunction: setStickyHeaders },
      [CANVAS_UNIFIED_COLUMNS]: {
        setterFunction: setUnifiedColumns,
        configPath: 'canvasUnifiedColumns',
      },
    };

    each(defaults, (metadata, stateKey) => {
      const { configPath, setterFunction } = metadata;

      const savedUserValue = gridPreferences.loadColumnState({ namePrefix: stateKey });
      const providedConfigValue = get(contextStore.clientConfig, configPath);

      const defaultFound = !isNil(savedUserValue) ? savedUserValue : providedConfigValue;
      if (!isNil(defaultFound)) return setterFunction(defaultFound);
    });
  }

  function setSidebarHeaderHeight(height) {
    state.sidebarHeaderHeight = height;
  }

  async function fetchFinalWorkpackages() {
    const [err, { data: finalWorkpackageIds }] = await to(
      axios.get('/api/assortment-canvases/final-workpackages')
    );

    if (err) throw new Error(err.message);

    state.finalWorkpackageIds = new Set(finalWorkpackageIds);
    return finalWorkpackageIds;
  }

  /**
   * Gets the final scenarios for the given workpackage IDs.
   * @param {Array<String> | undefined} wpIds The workpackackage IDs to get the final scenarios for.
   * When unset or empty, final scenarios for the selected workpackage will be retrieved instead.
   * @param {Object} options The options to customise the fetching behaviour.
   * @param {Boolean | undefined} skipCommit When true, the finalScenarioIds state will not be updated.
   * When false or unset, it will be updated.
   * @returns {Array<String>} The final scenario IDs.
   */
  async function fetchFinalScenarios(wpIds, options = {}) {
    // If no workpackage IDs are provided, fetch scenarios for the selected workpackage
    const workpackageIds = wpIds?.length ? wpIds : [workpackagesStore.selectedWorkpackage?._id];

    if (!workpackageIds[0]) {
      // No workpackage IDs available, return empty array
      return [];
    }

    const [err, { data: finalScenarioIds }] = await to(
      axios.get('/api/assortment-canvases/final-scenarios', { params: { workpackageIds } })
    );

    if (err) throw new Error(err.message);

    if (!options?.skipCommit) {
      state.finalScenarioIds = new Set(finalScenarioIds);
    }

    return finalScenarioIds;
  }

  /**
   * Fetches and sets both final scenarios and workpackages
   * This should be called on any operation where the final checkpoint state may change
   */
  async function updateFinalWpAndScenarioState() {
    await Promise.all([fetchFinalWorkpackages(), fetchFinalScenarios()]);
  }

  async function fetchCanvases(payload = {}) {
    const defaultPayload = { where: { live: true } };
    const params = payload.params || defaultPayload;
    const scenarioId = payload.scenarioId || scenariosStore.selectedScenario._id;
    state.loading = true;
    const [err, response] = await to(
      axios.get(`/api/assortment-canvases/scenario/${scenarioId}`, { params })
    );
    if (err) throw new Error(err.message);
    if (!payload.isStatelessFetch) state.assortmentCanvases = response.data;
    state.loading = false;
    updateFinalWpAndScenarioState();
    return response.data;
  }

  async function fetchCanvas({ canvasId, forceSelectedCanvasUpdate } = {}) {
    if (
      !state.selectedAssortmentCanvas._id ||
      // forceSelectedCanvasUpdate - enforces selected canvas refresh when one of the nested properties of the canvas got updated but is not reflected in the state
      (canvasId === state.selectedAssortmentCanvas._id && !forceSelectedCanvasUpdate)
    ) {
      return;
    }
    const scenarioId = scenariosStore.selectedScenario._id;
    state.loading = true;
    const [err, response] = await to(
      axios.get(`/api/assortment-canvases/scenario/${scenarioId}/canvas/${canvasId}`)
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    state.selectedAssortmentCanvas = response.data;
  }

  async function fetchCanvasesForSelectedWorkpackage() {
    const workpackageId = workpackagesStore.selectedWorkpackage._id;
    state.loading = true;
    const where = { live: true };
    const [err, res] = await to(
      axios.get(`/api/assortment-canvases/workpackage/${workpackageId}`, { params: { where } })
    );
    if (err) throw new Error(err.message);
    state.workpackageAssortmentCanvases = res.data;
    state.loading = false;
  }

  async function fetchConstraintsCopyTarget({ checkpointId }) {
    const [err, response] = await to(
      axios.get(`/api/assortment-canvases/${checkpointId}/constraints`)
    );

    state.constraintsCopyTarget = {};
    state.selectedConstraintsToCopy = {};
    if (err) throw new Error(err.message);
    state.spacebreaks.forEach((spacebreak, index) => {
      if (response.data.spacebreaks[index]) {
        response.data.spacebreaks[index].appliedTo = spacebreak;
      }
    });

    state.constraintsCopyTarget = response.data;
  }
  async function refreshReferenceCheckpointData() {
    // Pull the current spacebreak and product key for the reference checkpoints so we can compare them.
    // Get the reference checkpoint
    const { _id: canvasId, storeClassId } = state.selectedAssortmentCanvas;
    const scenarioId = scenariosStore.selectedScenario._id;
    const checkpointParams = {
      where: {
        storeClassId, // This should also take a clusterId after AOV3-622
        scenarioId,
        reference: true,
      },
      pick: [
        'checkpoint',
        'isObserved',
        'forecastParameters',
        'forecastResults',
        'jobs',
        'isNotRevertable',
      ],
      limit: 1,
    };
    state.loading = true;
    const [checkpointErr, checkpointResponse] = await to(
      axios.get(`/api/assortment-canvases/${canvasId}/checkpoints`, { params: checkpointParams })
    );
    if (checkpointErr) throw new Error(checkpointErr.message);

    // If no reference checkpoint exists, set these to null.
    // This covers the case where we've unset the reference checkpoint and need to remove our prior state.
    const checkpoint = checkpointResponse.data;
    if (!checkpoint.length) {
      state.referenceCheckpoint = null;
      state.selectedReferenceCheckpoint = null;
      state.referenceCheckpointProducts = {};
      state.loading = false;

      return;
    }

    // If a reference checkpoint exists, there will only be one. Set that as our reference and currently selected one.
    const referenceCheckpointResponse = checkpoint[0];
    state.referenceCheckpoint = referenceCheckpointResponse;
    state.selectedReferenceCheckpoint = referenceCheckpointResponse._id;

    const referenceProductParams = { pick: ['currentSpacebreakId', 'productKey'] };

    const [referenceProductsErr, referenceProductsResponse] = await to(
      axios.get(
        `/api/assortment-canvas-products/scenario/${scenarioId}/canvas/${referenceCheckpointResponse._id}`,
        { params: referenceProductParams }
      )
    );
    if (referenceProductsErr) throw new Error(referenceProductsErr.message);

    // Make a map of { productKey: currentSpacebreak } so we can quickly lookup differences
    // between the current placement and that of the reference products
    const referenceProducts = referenceProductsResponse.data;
    const productKeyCurrentSpacebreakIdMap = referenceProducts.reduce(
      (a, c) => set(a, c.productKey, c.currentSpacebreakId),
      {}
    );
    state.referenceCheckpointProducts = productKeyCurrentSpacebreakIdMap;
    state.loading = false;
  }

  async function updateAssortmentCanvas({ canvas = {} }) {
    state.loading = true;
    const canvasId = canvas._id;
    const [err, response] = await to(
      axios.patch(`/api/assortment-canvases/${canvasId}`, { canvas })
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    return response.data;
  }

  async function updateAssortmentCanvasConstraint({ canvasId, constraintId, constraints }) {
    state.loading = true;
    const [err, response] = await to(
      axios.post(`/api/assortment-canvases/${canvasId}/constraints/${constraintId || ''}`, {
        constraints,
      })
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
    return response.data;
  }

  async function removeAssortmentCanvasConstraint({ canvasId, constraintId }) {
    state.loading = true;
    const [err] = await to(
      axios.delete(`/api/assortment-canvases/${canvasId}/constraint/${constraintId}`)
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }
  async function removeAssortmentCanvasConstraintsForSpacebreak({ canvasId, spacebreakId }) {
    state.loading = true;
    const [err] = await to(
      axios.delete(`/api/assortment-canvases/${canvasId}/spacebreak/${spacebreakId}/constraint`)
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }
  async function fetchCanvasProducts(canvasId) {
    state.loading = true;
    const scenarioId = scenariosStore.selectedScenario._id;
    const [err, { data: assortmentCanvasProductsResponse } = {}] = await to(
      axios.get(`/api/assortment-canvas-products/scenario/${scenarioId}/canvas/${canvasId}/all`)
    );
    if (err) throw new Error(err.message);

    const { showInactiveProductsOnCanvas } = useFeature();

    // TODO this filtering logic should be done on the server side to avoid fetching unnecessary data
    // This was done as a quick fix to avoid changing the API contract at the time
    // TODO add tests for this when merging to master branch
    let productsToKeep = assortmentCanvasProductsResponse;
    // If the config is set to not show inactive products
    // (not in assortment and not in store class), filter them out
    if (!showInactiveProductsOnCanvas.value) {
      productsToKeep = (assortmentCanvasProductsResponse || []).filter(
        product => product.storeClassId && product.assortment
      );
    }

    setAssortmentCanvasProducts(productsToKeep);
    state.loading = false;
  }
  async function fetchCurrentCanvasInformation() {
    if (isEmpty(state.selectedAssortmentCanvas)) return;
    await Promise.all([
      refreshReferenceCheckpointData(),
      fetchCanvas({
        canvasId: state.selectedAssortmentCanvas._id,
        forceSelectedCanvasUpdate: true,
      }),
    ]);

    // after refreshing the checkpoints products, we pull the products and have their deltas calculated
    await fetchCanvasProducts(state.selectedAssortmentCanvas._id);
    state.canvasRenderingStatus = false;
  }
  async function revertCheckpointToCanvas({ checkpointId }) {
    state.loading = true;
    const [err, response] = await to(
      axios.post(`/api/assortment-canvases/checkpoints/${checkpointId}/revert-to`)
    );
    state.loading = false;
    if (err || (response.data && response.data.errors)) {
      throw new Error(`Unable to revert Assortment Canvas: ${response.id}`);
    }

    await fetchCurrentCanvasInformation();
    return response.data;
  }

  async function deleteAssortmentCanvas({ canvasId }) {
    state.loading = true;
    const [err, { data: result }] = await to(axios.delete(`/api/assortment-canvases/${canvasId}`));
    state.loading = false;
    if (err) throw new Error(err.message);
    return result;
  }

  async function fetchCheckpoints() {
    const canvasId = state.selectedAssortmentCanvas._id;
    const params = {
      where: {
        live: false,
        scenarioId: state.selectedAssortmentCanvas.scenarioId,
      },
      sortField: 'checkpointMeta.createdDate',
      pick: [
        'checkpointMeta',
        'isObserved',
        'forecastParameters',
        'forecastResults',
        'jobs',
        'reference',
        'isNotRevertable',
        'isFinalCheckpoint',
      ],
    };
    state.loading = true;
    const [err, response] = await to(
      axios.get(`/api/assortment-canvases/${canvasId}/checkpoints`, { params })
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    state.assortmentCheckpoints = response.data;
  }

  async function setReferenceCheckpoint({ oldReferenceCheckpointId, newReferenceCheckpointId }) {
    const canvasId = state.selectedAssortmentCanvas._id;
    state.loading = true;
    const [err] = await to(
      axios.post(`/api/assortment-canvases/${canvasId}/checkpoints/set-reference/`, {
        params: {
          oldReferenceCheckpointId,
          newReferenceCheckpointId,
        },
      })
    );
    state.loading = false;
    if (err) throw new Error(err.message);
  }

  async function fetchScenarioCheckpoints(options = {}) {
    const scenarioId = options.scenarioId || scenariosStore.selectedScenario._id;
    const where = {
      live: false,
      excludeObserved: get(options, 'excludeObserved', true),
    };
    const params = {
      where,
      sortField: 'checkpointMeta.createdDate',
      // need 'clusterId', 'storeClassId' for scenarioCheckpointsByCanvasId
      pick: ['_id', 'checkpointMeta', 'isObserved', 'clusterId', 'storeClassId', 'hasBeenForecast'],
    };
    state.loading = true;
    const [err, res] = await to(axios.get(`/api/scenarios/${scenarioId}/checkpoints`, { params }));
    if (err) throw new Error(err.message);
    if (!options.isStatelessFetch) state.scenarioCheckpoints = res.data;
    state.loading = false;
    return res.data;
  }

  async function fetchCheckpointsForSelectedWorkpackage(options = {}) {
    const workpackageId = options.workpackageId || workpackagesStore.selectedWorkpackage._id;
    state.loading = true;
    const params = {
      where: { live: false, excludeObserved: true },
      pick: ['scenarioId', 'clusterId', 'storeClassId', 'checkpointMeta'].concat(options.pickExtra),
    };
    const [err, res] = await to(
      axios.get(`/api/workpackages/${workpackageId}/checkpoints`, { params })
    );
    if (err) throw new Error(err.message);
    state.loading = false;
    if (!options.shouldCommit) return res.data;
    state.workpackageCheckpoints = res.data;
  }

  async function addCheckpoint() {
    state.loading = true;
    const canvasId = state.selectedAssortmentCanvas._id;
    const [err] = await to(axios.post(`/api/assortment-canvases/${canvasId}/checkpoint`));
    state.loading = false;
    if (err) throw new Error(err.message);
  }

  async function updateCheckpoint({ checkpointId, updates }) {
    state.loading = true;
    const [err] = await to(axios.patch(`/api/assortment-canvases/${checkpointId}/meta`, updates));
    state.loading = false;
    if (err) throw new Error(err.message);
  }

  async function deleteCheckpoint({ canvasId }) {
    state.loading = true;
    const [err, response] = await to(
      axios.delete(`/api/assortment-canvases/${canvasId}/checkpoint`)
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    return response.data;
  }

  async function runForecasterJob(payload) {
    const { checkpointId, scenarioId } = payload;
    state.loading = true;
    const [err, response] = await to(
      axios.post(`/api/assortment-canvases/scenario/${scenarioId}/${checkpointId}/forecaster`, {})
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    handleErrorMessages({ response });
  }

  async function runAssortmentOptimisation(payload) {
    state.loading = true;
    const [err, response] = await to(
      axios.post('/api/assortment-canvases/canvas-optimisation', payload)
    );
    state.loading = false;
    handleErrorMessages({ response });
    if (get(response, 'data.messageType') === 'JobValidation') {
      return get(response, 'data.data');
    }
    if (err) throw new Error(get(err, 'message'));
  }

  async function updateCanvasProduct(updates = {}) {
    state.saveInProgress = true;
    const { locallyUpdatedProduct, ...patchUpdates } = updates;
    // ensure timely update of product tile on the client side first

    updateAssortmentCanvasProductById(locallyUpdatedProduct);

    // ensure update of product tile on the server side for persistence
    const [err, response] = await to(
      axios.patch(`/api/assortment-canvas-products/${patchUpdates._id}`, patchUpdates)
    );
    if (err) throw new Error(err.message);
    state.saveInProgress = false;
    updateAssortmentCanvasProductById(response.data.canvasProduct);
    state.selectedAssortmentCanvas.constraints = response.data.constraints;
  }
  function changeLockType({ product, isLocked }) {
    const lockType = isLocked
      ? assortmentProductLockTypes.lockTypes.unlocked
      : assortmentProductLockTypes.lockTypes.locked;
    updateCanvasProduct({
      // locallyUpdatedProduct is meant to be used before the changes are done on the server,
      // to avoid product dragging flickering
      locallyUpdatedProduct: { ...product, lockType },
      _id: product._id,
      lockType,
    });
  }

  async function updateCanvasProducts({
    productsBeforeUpdates,
    locallyUpdatedProducts,
    updates,
    successMessage = 'actions.saveSuccess',
    failedMessage = null,
    showToastMessages = true,
  }) {
    // show error if there is nothing to update
    if (!size(locallyUpdatedProducts) && !size(updates)) {
      snackbarStore.showError(i18n.t(failedMessage));
      return;
    }
    state.saveInProgress = true;
    // ensure timely update of product tiles on the client side first
    updateAssortmentCanvasProducts(locallyUpdatedProducts);

    // ensure update of product tiles on the server side for persistence
    const [err, response] = await to(axios.patch('/api/assortment-canvas-products', updates));
    if (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', failedMessage));
      if (showToastMessages) {
        snackbarStore.showError(message);
      }
      // reset local state if error in updating products
      updateAssortmentCanvasProducts(productsBeforeUpdates);
      state.saveInProgress = false;
      return Promise.reject(message);
    }
    state.saveInProgress = false;
    state.selectedAssortmentCanvas.constraints = response.data.constraints;
    if (showToastMessages) {
      snackbarStore.showSuccess(i18n.t(successMessage));
    }
  }

  async function setSelectedAssortmentCanvas(canvas) {
    state.selectedAssortmentCanvas = canvas;
  }

  async function runCanvasGeneration(payload) {
    const { scenarioId, selectedCanvases } = payload;
    state.loading = true;
    const url = `/api/assortment-canvases/scenario/${scenarioId}/canvas-generation`;
    const [err, response] = await to(axios.post(url, selectedCanvases));
    state.loading = false;
    if (err) throw new Error(err.message);
    handleErrorMessages({ response });
  }

  function canvasGenerationComplete({ message }) {
    const selectedScenario = scenariosStore.selectedScenario;
    if (message.scenarioId === selectedScenario._id) {
      fetchCanvases({ params: { where: { live: true } } });
    }
    const content = i18n.t('notifications.canvasGeneration.finished');
    snackbarStore.showSuccess(content);
  }

  function canvasGenerationFailed() {
    const content = i18n.t('notifications.canvasGeneration.failed');
    snackbarStore.showError(content);
  }
  function changeCanvasRenderingStatus(status) {
    state.canvasRenderingStatus = status;
  }

  function refreshProductDeltas() {
    setAssortmentCanvasProducts(state.assortmentCanvasProducts);
  }
  async function fetchProductTopPartners({ productKey, cannGroupId } = {}) {
    state.selectedProductTopPartners = [];
    const scenarioId = scenariosStore.selectedScenario._id;
    const clusterId = state.selectedAssortmentCanvas.clusterId;
    const storeClassIdLocal = state.selectedAssortmentCanvas.storeClassId;
    state.loading = true;
    const [err, response] = await to(
      axios.get(
        `/api/assortment-canvas-products/scenario/${scenarioId}/cluster/${clusterId}/store-class/${storeClassIdLocal}/cann-group/${cannGroupId}/top-partners/${productKey}`
      )
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    state.selectedProductTopPartners = response.data;
  }

  async function fetchProductTransferredSpend({ productKey } = {}) {
    state.selectedProductTransferredSpend = {};
    const scenarioId = scenariosStore.selectedScenario._id;
    const canvasId = state.selectedAssortmentCanvas._id;
    state.loading = true;
    const [err, response] = await to(
      axios.get(
        `/api/assortment-canvas-products/scenario/${scenarioId}/canvas/${canvasId}/transferred-spend/${productKey}`
      )
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    state.selectedProductTransferredSpend = response.data;
  }

  async function updateConstraintsRanking({ canvasId, constraints, spacebreakId }) {
    state.loading = true;
    const [err] = await to(
      axios.patch(
        `/api/assortment-canvases/${canvasId}/spacebreak/${spacebreakId}/constraints/order`,
        { constraints }
      )
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }

  async function fetchProductDistribution({ productKey, canvases: canvasIds } = {}) {
    const scenarioId = scenariosStore.selectedScenario._id;
    state.loading = true;
    const [err, response] = await to(
      axios.get(
        `/api/assortment-canvas-products/scenario/${scenarioId}/product-distribution/${productKey}`,
        { params: canvasIds }
      )
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    return response.data;
  }

  async function toggleSpacebreakLockForSelectedCanvas({ spacebreakId, isLocked }) {
    const spacebreakRank = state.spacebreaksOrderedObject[spacebreakId].rank;
    const updatedCanvas = {
      _id: state.selectedAssortmentCanvas._id,
      spacebreaks: state.selectedAssortmentCanvas.spacebreaks.map(sb => {
        const currentSpacebreakRank = state.spacebreaksOrderedObject[sb._id].rank;
        if (currentSpacebreakRank > spacebreakRank) return sb;
        if (currentSpacebreakRank < spacebreakRank && !isLocked) return sb;
        // Otherwise we are locking a spacebreak.
        // This means the matching spacebreak and the spacebreaks with a rank smaller than it should be locked.
        return {
          ...sb,
          isLocked,
        };
      }),
    };
    const canvas = await updateAssortmentCanvas({ canvas: updatedCanvas });
    state.selectedAssortmentCanvas = {
      ...state.selectedAssortmentCanvas,
      spacebreaks: canvas.spacebreaks,
    };
  }

  async function compareCheckpointAndCanvas({ checkpointId, canvasId }) {
    state.loading = true;
    const [err, response] = await to(
      axios.get(`/api/assortment-canvases/from/${checkpointId}/to/${canvasId}/copy-eligible`)
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    return response.data;
  }

  async function copyCheckpointDecisionsToCanvas({
    checkpointId,
    canvasId,
    successMessage = 'messages.copyDecisionsSuccess',
    failedMessage = 'messages.copyDecisionsFailed',
  }) {
    state.saveInProgress = true;
    const dateFormats = contextStore.getDateFormats;
    try {
      await jobApi.runFunction('copy-checkpoint-decisions-to-canvas', {
        checkpoint_id: checkpointId,
        canvas_id: canvasId,
        localisations: {
          dateFormat: get(dateFormats, 'longAnalyticsWithTime'),
          translations: {
            preCopyDecisionsCheckpoint: i18n.t('assortmentCanvas.preCopyDecisionsCheckpoint'),
          },
        },
      });
      if (state.selectedAssortmentCanvas._id === canvasId) {
        state.canvasRenderingStatus = true;
        await fetchCurrentCanvasInformation();
        state.canvasRenderingStatus = false;
      }
      state.saveInProgress = false;
      snackbarStore.showSuccess(i18n.t(successMessage));
    } catch (err) {
      const key = get(err, 'response.data.messageKey', err.message);
      const message = i18n.t(key, get(err, 'response.data.messageParams', failedMessage));
      snackbarStore.showError(i18n.t(failedMessage));
      state.saveInProgress = false;
      throw new Error(message);
    }
  }

  function setListViewColumns(columns) {
    state.listViewColumns = columns;
  }

  function setCanvasCannGroupColumns(columns) {
    state.canvasCannGroupColumns = columns;
  }

  function initializeListViewColumns() {
    // First, try to load the column state from local storage
    const scenarioKey = scenariosStore.selectedScenario._id;
    const savedColumnState = gridPreferences.loadColumnState({
      namePrefix: LIST_VIEW_COLUMN_STORAGE_PREFIX,
      stateId: scenarioKey,
    });
    // Get the id of the scenario that got its canvas list view state saved most recently
    const lastStateSavedScenario = gridPreferences.loadColumnState({
      namePrefix: LIST_VIEW_LAST_STATE_SAVED_SCENARIO_PREFIX,
      stateId: 0,
    });

    // Get the initial columns from the getter
    const initialColumns = initialListViewColumns.value;
    let updatedColumns;

    if (savedColumnState) {
      // If a saved column state exists, merge it with the initial columns
      updatedColumns = gridPreferences.mergeGenericColumnStates(
        savedColumnState,
        initialColumns,
        'title'
      );
    } else if (lastStateSavedScenario) {
      // If a saved column state doesn't exist, find latest saved state and merge it with the initial columns
      const lastSavedColumnState = gridPreferences.loadColumnState({
        namePrefix: LIST_VIEW_COLUMN_STORAGE_PREFIX,
        stateId: lastStateSavedScenario,
      });
      updatedColumns = gridPreferences.mergeGenericColumnStates(
        lastSavedColumnState,
        initialColumns,
        'title'
      );
    } else {
      // Otherwise, use the initial columns from the getter
      updatedColumns = initialListViewColumns.value;
    }

    setListViewColumns(updatedColumns);
  }

  function initializeCanvasCannGroupColumns() {
    // First, try to load the column state from local storage
    const scenarioKey = scenariosStore.selectedScenario._id;
    const savedColumnState = gridPreferences.loadColumnState({
      namePrefix: CANN_GROUPS_COLUMN_STORAGE_PREFIX,
      stateId: scenarioKey,
    });

    // Get the initial columns from the getter
    const initialColumns = initialCanvasCannGroupColumns.value;

    let updatedColumns;

    if (savedColumnState) {
      // If a saved column state exists, merge it with the initial columns on 'key' field
      updatedColumns = gridPreferences.mergeGenericColumnStates(
        savedColumnState,
        initialColumns,
        'key'
      );
    } else {
      // Otherwise, use the initial columns from the getter
      updatedColumns = initialColumns;
    }

    setCanvasCannGroupColumns(updatedColumns);
  }

  function reorderListViewColumns(newOrder) {
    const scenarioKey = scenariosStore.selectedScenario._id;

    // Create a new array with all columns (visible and hidden) reordered
    const reorderedColumns = newOrder.map(newCol => {
      return state.listViewColumns.find(col => col.id === newCol.id);
    });

    // Set the reordered columns to the state
    setListViewColumns(reorderedColumns);

    // Save the new column state
    gridPreferences.saveColumnState(
      {
        namePrefix: LIST_VIEW_COLUMN_STORAGE_PREFIX,
        stateId: scenarioKey,
      },
      reorderedColumns
    );

    // update scenario which was modified the last,
    // it will be used to initialise the view for new scenarios (so we store scenarioKey as data)
    gridPreferences.saveColumnState(
      {
        namePrefix: LIST_VIEW_LAST_STATE_SAVED_SCENARIO_PREFIX,
        stateId: 0,
      },
      scenarioKey
    );
  }

  // TODO refactor into single method
  function reorderCanvasCannGroupColumns(newOrder) {
    const scenarioKey = scenariosStore.selectedScenario._id;

    // Create a new array with all columns (visible and hidden) reordered
    const reorderedColumns = newOrder.map((newCol, idx) => {
      const foundCol = state.canvasCannGroupColumns.find(col => col.key === newCol.key);
      return { ...foundCol, order: idx };
    });

    // Commit the reordered columns to the state
    setCanvasCannGroupColumns(reorderedColumns);

    // Save the new column state
    gridPreferences.saveColumnState(
      {
        namePrefix: CANN_GROUPS_COLUMN_STORAGE_PREFIX,
        stateId: scenarioKey,
      },
      reorderedColumns
    );
  }

  function toggleListViewColumnVisibility(selectedColumn) {
    const scenarioKey = scenariosStore.selectedScenario._id;

    const updatedColumns = state.listViewColumns.map(col => {
      if (col.id === selectedColumn.id) {
        return { ...col, visible: !col.visible };
      }
      return col;
    });
    setListViewColumns(updatedColumns);
    // Save the new column state
    gridPreferences.saveColumnState(
      {
        namePrefix: LIST_VIEW_COLUMN_STORAGE_PREFIX,
        stateId: scenarioKey,
      },
      updatedColumns
    );

    gridPreferences.saveColumnState(
      {
        namePrefix: LIST_VIEW_LAST_STATE_SAVED_SCENARIO_PREFIX,
        stateId: 0,
      },
      scenarioKey
    );
  }

  function toggleCanvasCannGroupVisibility(selectedCannGroup) {
    const scenarioKey = scenariosStore.selectedScenario._id;

    const updatedColumns = state.canvasCannGroupColumns.map(col => {
      if (col.key === selectedCannGroup.key) {
        return { ...col, visible: !col.visible };
      }
      return col;
    });

    setCanvasCannGroupColumns(updatedColumns);
    // Save the new column state
    gridPreferences.saveColumnState(
      {
        namePrefix: CANN_GROUPS_COLUMN_STORAGE_PREFIX,
        stateId: scenarioKey,
      },
      updatedColumns
    );
  }

  async function setFinalCheckpoint({ checkpointId, value } = {}) {
    state.finalCheckpointLoading = true;
    const [err] = await to(
      axios.post(`/api/assortment-canvases/set-final-checkpoint/${checkpointId}`, {
        params: { value },
      })
    );

    if (err) {
      state.finalCheckpointLoading = false;

      throw err;
    }

    // Final state touches checkpoints, selected canvas data, and final scenario/workpackage badges.
    await fetchCheckpoints();
    await fetchCurrentCanvasInformation();
    await updateFinalWpAndScenarioState();
    state.finalCheckpointLoading = false;
  }

  function resetExpandedCdts() {
    state.expandedCdts = {};
  }

  function resetCannGroupExpandedCdts(cdtNodeIds) {
    state.expandedCdts = omit(state.expandedCdts, cdtNodeIds);
  }

  function setFullScreen(boolean) {
    state.fullScreenExpanded = boolean;
  }

  function setActiveSpacebreak(spacebreakId) {
    state.activeSpacebreak = spacebreakId;
  }

  function setSelectedPod(selectedPod) {
    state.selectedPod = selectedPod;
  }

  function setSpacebreaks(spacebreaksInput) {
    state.spacebreaks = spacebreaksInput;
    const spacebreaksOrderedObjectRaw = {};
    state.spacebreaks.forEach((s, rank) => {
      spacebreaksOrderedObjectRaw[s._id] = { rank: rank + 1, ...s };
    });
    state.spacebreaksOrderedObject = spacebreaksOrderedObjectRaw;
  }

  function setCanvasProductsFilters(filters) {
    state.canvasProductsFilters = filters;
  }

  function setCanvasProductsHighlights(highlights) {
    state.canvasProductsHighlights = highlights;
  }

  function closeSpacebreakSettingsPanel() {
    state.spacebreakSettings.open = false;
  }

  function setNumberOfRowsInGrid(rowCount) {
    state.numberOfRowsInGrid = rowCount;
  }

  function setCanvasProductsFiltersSelection(filters) {
    state.canvasProductsFiltersSelection = filters;
  }

  function setDisplayBy(displayOption) {
    state.displayBy = displayOption;
  }

  function setChildTree({ id, tree }) {
    state.childTrees[id] = tree;
  }

  function setCollapsedCdts({ cannGroupId, cdtId }) {
    // This is triggered whenever we collapse a CDT level.
    // This can happen at any point in the tree, from the direct parent to several levels up.
    // To handle this, we leverage our tree of children to set the depth of all collapsed elements to 0
    // Our height will then become the next highest value in the object.

    // convert Set to array, giving [cdt1, cdt2, cdt3, ...]
    const childrenOfCollapsedCdt = [...state.childTrees[cannGroupId][cdtId]];

    // Create obj like { cdt1: 0, cdt2: 0, cdt3: 0, ...}
    const zeroDepthCdts = childrenOfCollapsedCdt.reduce((a, c) => set(a, c, 0), {});
    // Merge onto existing structure, merge mutates but we're reassigning so that's fine here.
    state.expandedCdts = merge(state.expandedCdts, zeroDepthCdts);
  }

  function setExpandedCdts({ id, depth }) {
    // This is triggered every time we expand a CDT.
    // We set its depth which is the number of CDTs deep for that component.
    // This is used to set the header height across all the components.
    state.expandedCdts = { ...state.expandedCdts, [id]: depth };
  }

  function setIsProductSidebarOpen(value) {
    state.isProductSidebarOpen = value;
  }

  function openProductTooltip(product) {
    if (!state.openedProductTooltips.some(p => p.productKey === product.productKey)) {
      state.openedProductTooltips.push(product);
    }
  }

  function closeProductTooltip(productKey) {
    state.openedProductTooltips = state.openedProductTooltips.filter(
      p => p.productKey !== productKey
    );
  }

  function clearOpenedProductTooltips() {
    state.openedProductTooltips = [];
  }

  function setDashboardProduct(product) {
    state.selectedDashboardProduct = product;
  }

  function setConstraintEditing({ rowNodeId, editingBoolean }) {
    if (isEqual(state.constraintEditing.constraint.rowNodeId, rowNodeId)) {
      state.constraintEditing.constraint.open = editingBoolean;
    } else {
      state.constraintEditing.constraint.rowNodeId = rowNodeId;
      state.constraintEditing.constraint.open = editingBoolean;
    }
  }

  function toggleSpaceBreakSettingsPanel(spacebreak) {
    if (isEqual(state.spacebreakSettings.spacebreak, spacebreak)) {
      state.spacebreakSettings.open = !state.spacebreakSettings.open;
    } else {
      state.spacebreakSettings.spacebreak = spacebreak;
      state.spacebreakSettings.open = true;
    }
  }

  function setConstraintRowNodeData(rowData) {
    state.constraintEditing.constraint.rowNodeData = rowData;
  }

  function updateConstraintRowNodeData({ data, field }) {
    state.constraintEditing.constraint.rowNodeData = {
      ...state.constraintEditing.constraint.rowNodeData,
      [field]: data,
    };
  }

  function incrementPopUpZindex() {
    state.popUpZindex += 1;
  }

  function setShowDashboard(status) {
    state.showDashboard = status;
  }

  function beginCanvasProductDrag(ownerId) {
    if (!ownerId) {
      return;
    }

    state.activeCanvasProductDragOwnerId = ownerId;
    state.isCanvasProductDragInProgress = true;
  }

  function endCanvasProductDrag(ownerId) {
    if (!ownerId || state.activeCanvasProductDragOwnerId !== ownerId) {
      return false;
    }

    state.activeCanvasProductDragOwnerId = null;
    state.isCanvasProductDragInProgress = false;

    return true;
  }

  // Backward-compatible setter used by older code paths.
  // Prefer beginCanvasProductDrag/endCanvasProductDrag for owner-safe updates.
  function setCanvasProductDragInProgress(status) {
    if (status) {
      if (!state.activeCanvasProductDragOwnerId) {
        state.activeCanvasProductDragOwnerId = 'legacy-canvas-drag-owner';
      }
      state.isCanvasProductDragInProgress = true;
      return;
    }

    state.isCanvasProductDragInProgress = false;
    state.activeCanvasProductDragOwnerId = null;
  }

  function setProductTransferredSpend(transferredSpend) {
    state.selectedProductTransferredSpend = transferredSpend;
  }

  function setSelectedReferenceCheckpoint(selectedReferenceCheckpoint) {
    state.selectedReferenceCheckpoint = selectedReferenceCheckpoint;
  }

  function setShowFilterLegend(value) {
    state.showFilterLegend = value;
  }

  function setAdditionalAttributeColumns({ canvasId, selection }) {
    state.additionalAttributeColumns = {
      ...state.additionalAttributeColumns,
      [canvasId]: selection,
    };
  }

  function setExtraKPIsColumns({ canvasId, selection }) {
    state.additionalKPIsColumns = {
      ...state.additionalKPIsColumns,
      [canvasId]: selection,
    };
  }

  function setConstraintsValidationOccurred(value) {
    state.constraintsValidationOccurred = { ...state.constraintsValidationOccurred, ...value };
  }

  // We want to check localStorage immediately and set the state.
  setCanvasUserDefaults();

  return {
    // state
    ...toRefs(state),

    // getters
    isFinal,
    canvases,
    canvasProducts,
    getDashboardProduct,
    getSelectedProductTransferredSpend,
    getPointsOfDistribution,
    sortByProductsInCanvas,
    spaceUsedPerSpacebreak,
    productsPerSpacebreak,
    getProductsWithNoSpacebreak,
    checkpoints,
    isCanvasLockedByFinalCheckpoint,
    selectedCanvasLockedSpacebreaks,
    isSpacebreakLocked,
    isSpacebreakLockToggleDisabled,
    totalExpandedHeaderDepth,
    workpackageAssortmentCanvasesScenarioMap,
    scenarioCheckpointsById,
    scenarioCheckpointsByCanvasId,
    getCanvasProductsFiltersToFilter,
    getFilteredProducts,
    getCanvasProductsHighlightsToFilter,
    getHighlightedProducts,
    getIndexedCanvasProducts,
    getShowDashboard,
    isCanvasProductDragInProgress,
    getSelectedProductTopPartners,
    isCanvasConfigInvalidForOptimisation,
    wpCheckpointsMap,
    wpCanvasesMap,
    canvasesById,
    getProductTag,
    productsByCdtGroup,
    hasConstraintsToCopySelected,
    isConstraintSelectedToCopy,
    constraintsToCopyById,
    canvasProductsPallets,
    selectedCanvas,
    availableProductAttributesValues,
    availableProductSeries,
    filteredListViewKpiColumns,
    listViewExtraColumns,
    visibleListViewColumns,
    visibleCanvasCannGroups,
    constraintsValidationOccurred,
    // actions
    fetchCanvases,
    fetchCanvas,
    fetchCanvasesForSelectedWorkpackage,
    fetchConstraintsCopyTarget,
    refreshReferenceCheckpointData,
    updateAssortmentCanvas,
    updateAssortmentCanvasConstraint,
    removeAssortmentCanvasConstraint,
    removeAssortmentCanvasConstraintsForSpacebreak,
    fetchCanvasProducts,
    fetchCurrentCanvasInformation,
    revertCheckpointToCanvas,
    deleteAssortmentCanvas,
    fetchCheckpoints,
    setReferenceCheckpoint,
    fetchScenarioCheckpoints,
    fetchCheckpointsForSelectedWorkpackage,
    addCheckpoint,
    updateCheckpoint,
    deleteCheckpoint,
    runForecasterJob,
    runAssortmentOptimisation,
    updateAssortmentCanvasProductById,
    updateCanvasProduct,
    changeLockType,
    updateCanvasProducts,
    setSelectedAssortmentCanvas,
    runCanvasGeneration,
    canvasGenerationComplete,
    canvasGenerationFailed,
    changeCanvasRenderingStatus,
    refreshProductDeltas,
    fetchProductTopPartners,
    fetchProductTransferredSpend,
    updateConstraintsRanking,
    fetchProductDistribution,
    toggleSpacebreakLockForSelectedCanvas,
    compareCheckpointAndCanvas,
    copyCheckpointDecisionsToCanvas,
    resetExpandedCdts,
    resetCannGroupExpandedCdts,
    setTileSize,
    setDisplayBy,
    setFullScreen,
    setSelectedView,
    setSidebarHeaderHeight,
    setStickyHeaders,
    setAssortmentCanvasProducts,
    setUnifiedColumns,
    setActiveSpacebreak,
    setSpacebreaks,
    setSelectedPod,
    setCanvasProductsFilters,
    setCanvasProductsHighlights,
    closeSpacebreakSettingsPanel,
    setNumberOfRowsInGrid,
    setCanvasProductsFiltersSelection,
    setChildTree,
    setCollapsedCdts,
    setExpandedCdts,
    setIsProductSidebarOpen,
    openProductTooltip,
    closeProductTooltip,
    clearOpenedProductTooltips,
    setDashboardProduct,
    setConstraintEditing,
    toggleSpaceBreakSettingsPanel,
    setConstraintRowNodeData,
    updateConstraintRowNodeData,
    incrementPopUpZindex,
    setShowDashboard,
    beginCanvasProductDrag,
    endCanvasProductDrag,
    setCanvasProductDragInProgress,
    setProductTransferredSpend,
    setSelectedReferenceCheckpoint,
    setShowFilterLegend,
    setFinalCheckpoint,
    fetchFinalScenarios,
    fetchFinalWorkpackages,
    isWorkpackageFinal,
    isScenarioFinal,
    updateFinalWpAndScenarioState,
    setConstraintsValidationOccurred,

    // list view columns
    initializeListViewColumns,
    initializeCanvasCannGroupColumns,
    reorderListViewColumns,
    reorderCanvasCannGroupColumns,
    toggleListViewColumnVisibility,
    toggleCanvasCannGroupVisibility,
    setAdditionalAttributeColumns,
    setExtraKPIsColumns,
  };
});
